import { useTranslation } from 'react-i18next';

import { SCUICustomDataGrid } from '../../../atomic/molecules';
import { SCUIButton, SCUIDateRanger, SCUINoRecordComponent } from '../../../atomic/atoms';
import { Box, Chip, MenuItem, Select } from '@mui/material';

import TimeHelper from '../../../utils/time';

import { SendToAxaptaStatus } from '../enum/SummaryEnums';
import airdropShipment from '../../../assets/icons/svg/pageIcons/airdropShipment.svg';
import { SV, svCls } from '../summaryV2.classes';
import './style.scss';

export const SummaryList = ({
    summaryList,
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    handleSearch,
    isSearched,
    setSelectedSummary,
    handleAxapta,
    selectedSummary,
    status,
    setStatus,
}: any) => {
    const { t } = useTranslation();

    const getStatusTag = (rowStatus?: number | null) => {
        switch (rowStatus) {
            case SendToAxaptaStatus.PendingTransfer:
                return (
                    <Chip
                        className={svCls(SV.statusChip, SV.statusChipPendingAccounting)}
                        sx={{
                            backgroundColor: '#fff3cd !important',
                            '& .MuiChip-label': {
                                color: '#c1ad61',
                                fontWeight: 'bold',
                            },
                        }}
                        label={t('summary.pendingAccounting')}
                    />
                );
            case SendToAxaptaStatus.TransferCompleted:
                return (
                    <Chip
                        className={svCls(SV.statusChip, SV.statusChipAccountingComplete)}
                        sx={{
                            backgroundColor: '#22aeee !important',
                            '& .MuiChip-label': {
                                color: '#004a88',
                                fontWeight: 'bold',
                            },
                        }}
                        label={t('summary.accountingComplate')}
                    />
                );
            case SendToAxaptaStatus.SendToAxapta:
                return (
                    <Chip
                        className={svCls(SV.statusChip, SV.statusChipTransferComplete)}
                        label={t('summary.transferToAxaptaComplete')}
                        sx={{
                            backgroundColor: '#cdead8 !important',
                            '& .MuiChip-label': {
                                color: '#50755f',
                                fontWeight: 'bold',
                            },
                        }}
                    />
                );
            case SendToAxaptaStatus.RequiredForCalculation:
                return (
                    <Chip
                        className={svCls(SV.statusChip, SV.statusChipNeedRecalculate)}
                        label={t('summary.needRecalculate')}
                        sx={{
                            backgroundColor: '#aa6861 !important',
                            '& .MuiChip-label': {
                                color: '#3c2c2c',
                                fontWeight: 'bold',
                            },
                        }}
                    />
                );
            default:
                return <>-</>;
        }
    };

    const paymentStatusOptions = [
        { value: SendToAxaptaStatus.All, label: t('summary.all') },
        { value: SendToAxaptaStatus.PendingTransfer, label: t('summary.pendingAccounting') },
        { value: SendToAxaptaStatus.TransferCompleted, label: t('summary.accountingComplate') },
        { value: SendToAxaptaStatus.SendToAxapta, label: t('summary.transferToAxaptaComplete') },
        { value: SendToAxaptaStatus.RequiredForCalculation, label: t('summary.needRecalculate') },
    ];

    const columns = [
        {
            field: 'id',
            headerName: t('summary.id'),
            flex: 1,
            align: 'center',
            headerAlign: 'center',
        },
        {
            field: '',
            headerName: t('summary.summaryDate'),
            align: 'center',
            headerAlign: 'center',
            flex: 1,
            renderCell: (values: any) => {
                return `${TimeHelper.formatDate(values?.row?.periodBeginDate)} / ${TimeHelper.formatDate(values?.row?.periodEndDate)}`;
            },
        },
        {
            field: 'sendToAxaptaDate',
            headerName: t('summary.axaptaDate'),
            align: 'center',
            headerAlign: 'center',
            flex: 1,
            renderCell: (values: any) => {
                return values?.row?.sendToAxaptaDate ? TimeHelper.formatDate(values?.row?.sendToAxaptaDate) : '-';
            },
        },
        {
            field: 'sendToAxaptaStatus',
            headerName: t('summary.axaptaStatus'),
            align: 'center',
            headerAlign: 'center',
            flex: 1,
            renderCell: (values: any) => {
                return getStatusTag(values?.row?.sendToAxaptaStatus);
            },
        },
        {
            field: 'edit',
            headerName: t('general.edit'),
            align: 'center',
            headerAlign: 'center',
            flex: 1,
            renderCell: (values: any) => {
                return (
                    <SCUIButton
                        text={t('general.edit')}
                        type="primary"
                        className={svCls('summary-edit-button', SV.btnEdit)}
                        sizeType="fixed"
                        buttonType="button"
                        size={'m'}
                        fullwidth
                        onClick={() => handleGoDetail(values?.row)}
                    />
                );
            },
        },
        {
            field: 'transferToAxapta',
            align: 'center',
            headerAlign: 'center',
            headerName: t('summary.transferToAxapta'),
            flex: 1,
            renderCell: (values: any) => {
                return (
                    <SCUIButton
                        text={t('summary.transferToAxapta')}
                        className={svCls('summary-axapta-button', SV.btnAxapta)}
                        type="primary"
                        sizeType="fixed"
                        buttonType="button"
                        size={'m'}
                        fullwidth
                        disabled={values?.row?.sendToAxaptaStatus === SendToAxaptaStatus.TransferCompleted}
                        onClick={() => handleAxapta(values?.row?.id)}
                    />
                );
            },
        },
    ];

    const _handlChangeDate = (event: any) => {
        setStartDate(event[0]);
        setEndDate(event[1]);
    };

    const handleGoDetail = (detail: any) => {
        setSelectedSummary(detail);
    };

    return !selectedSummary?.id ? (
        <Box className={SV.list}>
            <Box
                className={SV.listSearchBar}
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    margin: '24px 0 32px 0',
                    '& .sc-btn__text': {
                        padding: '8px 16px !important',
                    },
                }}
            >
                <p
                    className={SV.listSearchLabel}
                    style={{ fontSize: '14px', fontWeight: '600', marginRight: '12px' }}
                >
                    {t('summary.summaryDateRange')}:
                </p>
                <Box className={svCls(SV.listDateRanger, SV.dateRange)}>
                    <SCUIDateRanger
                        fromDate={startDate}
                        toDate={endDate}
                        setDateRange={_handlChangeDate}
                        dateFormat="YYYY-MM-DD"
                    />
                </Box>
                <p
                    className={SV.listSearchLabel}
                    style={{ fontSize: '14px', fontWeight: '600', marginLeft: '24px' }}
                >
                    {t('summary.axaptaStatus')}:
                </p>
                <Select
                    value={status}
                    onChange={(event: any) => {
                        setStatus(event.target.value);
                    }}
                    sx={{ marginLeft: '12px' }}
                >
                    {(paymentStatusOptions || []).reverse().map((item: any) => (
                        <MenuItem key={item.value} id={item.value} value={item.value}>
                            {item.label}
                        </MenuItem>
                    ))}
                </Select>
                <SCUIButton
                    className={SV.btnSearch}
                    style={{ marginLeft: '24px' }}
                    text={t('general.search')}
                    type="primary"
                    sizeType="fixed"
                    size={'m'}
                    disabled={!startDate || !endDate}
                    onClick={() => handleSearch()}
                />
            </Box>
            <Box className={SV.listGridContainer}>
                {!isSearched ? (
                    <Box className={SV.listNoRecord}>
                        <SCUINoRecordComponent img={airdropShipment} />
                    </Box>
                ) : (
                    <Box className={SV.listGrid}>
                        <SCUICustomDataGrid
                            getRowClassName={'summary-list-grid'}
                            rows={summaryList || []}
                            columns={columns}
                            rowHeight={75}
                            hideFooter
                            allrecord
                        />
                    </Box>
                )}
            </Box>
        </Box>
    ) : (
        <></>
    );
};
